"use client";

import { useState, lazy, Suspense } from "react";
import { FaMinus, FaPlus } from "react-icons/fa";
import { useControlState } from "../trade-order/hooks/useControlState";

const PositionSelectionScreen = lazy(
  () => import("./components/PositionSelectionScreen"),
);

interface MarketDepthScreenProps {
  onBack: () => void;
  positionId: string;
  symbol: string;
  currentVolume?: number;
  /** Existing stop-loss price for the position, if any. */
  currentSl?: number | null;
  /** Existing take-profit price for the position, if any. */
  currentTp?: number | null;
  /** Price increment applied by the SL/TP steppers. */
  priceStep?: number;
  onSell?: (volume: number) => void;
  onBuy?: (volume: number) => void;
  onClose?: (volume: number, positionId: string) => void;
}

export default function MarketDepthScreen({
  onBack,
  positionId,
  symbol,
  currentVolume = 0.01,
  currentSl = null,
  currentTp = null,
  priceStep = 0.0001,
  onSell,
  onBuy,
  onClose,
}: MarketDepthScreenProps) {
  // null = not set → show an empty affordance instead of literal "sl"/"tp".
  const [slValue, setSlValue] = useState<number | null>(currentSl);
  const [tpValue, setTpValue] = useState<number | null>(currentTp);
  const [volume, setVolume] = useState<number>(currentVolume);

  // Precision derived from the price step (e.g. 0.0001 → 4 decimals).
  const pricePrecision = Math.max(
    0,
    (priceStep.toString().split(".")[1] || "").length
  );
  const formatPrice = (v: number) => v.toFixed(pricePrecision);

  const stepPrice = (prev: number | null, delta: number): number => {
    // First interaction on an unset field seeds it at one step from zero.
    const next = (prev == null ? 0 : prev) + priceStep * delta;
    return Number(Math.max(0, next).toFixed(pricePrecision));
  };

  const handleSlChange = (delta: number) => {
    setSlValue((prev) => stepPrice(prev, delta));
  };

  const handleTpChange = (delta: number) => {
    setTpValue((prev) => stepPrice(prev, delta));
  };

  // Shared control-state model (proposal 10). While a field is unset there is
  // nothing to decrease, so the "−" stepper is disabled (aria-disabled); the
  // "+" seeds the field from zero and stays interactive.
  const slDecControl = useControlState({ isDisabled: slValue == null });
  const tpDecControl = useControlState({ isDisabled: tpValue == null });
  const [showPositionSelection, setShowPositionSelection] = useState(false);
  const [selectedPositionId, setSelectedPositionId] = useState(positionId);

  const handleVolumeChange = (newVolume: number) => {
    // Ensure volume is not negative and has proper decimal places
    const formattedVolume = Math.max(0.01, Number(newVolume.toFixed(2)));
    setVolume(formattedVolume);
  };

  const handleSell = () => {
    if (onSell) {
      onSell(volume);
    }
  };

  const handleBuy = () => {
    if (onBuy) {
      onBuy(volume);
    }
  };

  const handleClose = () => {
    if (onClose) {
      onClose(volume, selectedPositionId);
    }
  };

  const handlePositionSelect = (
    selectedPositionId: string,
    action: "sell" | "buy" | "close"
  ) => {
    setSelectedPositionId(selectedPositionId);
    setShowPositionSelection(false);
    // Handle the selected position action
    switch (action) {
      case "sell":
        handleSell();
        break;
      case "buy":
        handleBuy();
        break;
      case "close":
        handleClose();
        break;
    }
  };

  if (showPositionSelection) {
    return (
      <Suspense fallback={null}>
        <PositionSelectionScreen
          onBack={() => setShowPositionSelection(false)}
          symbol={symbol}
          onSell={(positionId) => handlePositionSelect(positionId, "sell")}
          onBuy={(positionId) => handlePositionSelect(positionId, "buy")}
          onClose={(positionId) => handlePositionSelect(positionId, "close")}
        />
      </Suspense>
    );
  }

  return (
    <div className="h-full bg-background text-foreground flex flex-col">
      {/* Header */}
      <div className="flex justify-between items-center px-4 py-3">
        <button onClick={onBack} className="text-blue-400 text-lg">
          Back
        </button>
        <button
          onClick={() => setShowPositionSelection(true)}
          className="flex items-center gap-1"
        >
          <span className="text-blue-400 text-lg">#{selectedPositionId}</span>
          <svg
            width="12"
            height="8"
            viewBox="0 0 12 8"
            fill="none"
            className="ml-1"
          >
            <path
              d="M1 1L6 6L11 1"
              stroke="#4B9BFF"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <span className="text-blue-400 text-lg">Lots</span>
      </div>

      {/* Controls Row */}
      <div className="flex items-center justify-between px-4 py-3">
        {/* SL Control */}
        <div className="flex items-center gap-2">
          <button
            onClick={slDecControl.guard(() => handleSlChange(-1))}
            aria-label="Decrease stop loss"
            disabled={slDecControl.isDisabled}
            className={`text-blue-400 text-2xl p-1 ${slDecControl.isDisabled ? "is-disabled-glyph" : ""}`}
            {...slDecControl.ariaAttrs}
          >
            <FaMinus />
          </button>
          <div className="px-3 py-1 min-w-[64px] text-center">
            <div className="text-muted-foreground text-[10px] uppercase tracking-wider">
              SL
            </div>
            <span className="text-foreground text-lg">
              {slValue == null ? "—" : formatPrice(slValue)}
            </span>
          </div>
          <button
            onClick={() => handleSlChange(1)}
            aria-label="Increase stop loss"
            className="text-blue-400 text-2xl p-1"
          >
            <FaPlus />
          </button>
        </div>

        {/* Volume Control */}
        <div className="flex items-center bg-transparent">
          <button
            onClick={() => handleVolumeChange(volume - 0.01)}
            className="text-blue-400 text-2xl p-2"
          >
            <FaMinus />
          </button>
          <div className="px-6 text-center">
            <span className="text-foreground text-2xl font-light">
              {volume.toFixed(2)}
            </span>
          </div>
          <button
            onClick={() => handleVolumeChange(volume + 0.01)}
            className="text-blue-400 text-2xl p-2"
          >
            <FaPlus />
          </button>
        </div>

        {/* TP Control */}
        <div className="flex items-center gap-2">
          <button
            onClick={tpDecControl.guard(() => handleTpChange(-1))}
            aria-label="Decrease take profit"
            disabled={tpDecControl.isDisabled}
            className={`text-blue-400 text-2xl p-1 ${tpDecControl.isDisabled ? "is-disabled-glyph" : ""}`}
            {...tpDecControl.ariaAttrs}
          >
            <FaMinus />
          </button>
          <div className="px-3 py-1 min-w-[64px] text-center">
            <div className="text-muted-foreground text-[10px] uppercase tracking-wider">
              TP
            </div>
            <span className="text-foreground text-lg">
              {tpValue == null ? "—" : formatPrice(tpValue)}
            </span>
          </div>
          <button
            onClick={() => handleTpChange(1)}
            aria-label="Increase take profit"
            className="text-blue-400 text-2xl p-1"
          >
            <FaPlus />
          </button>
        </div>
      </div>

      {/* Market Depth Grid */}
      <div className="flex-1 overflow-hidden">
        <div className="h-full grid grid-cols-2">
          {/* Bid Side (Left) */}
          <div className="border-r border-border">
            <div className="h-full flex flex-col">
              {/* Bid levels - Blue gradient */}
              {[...Array(20)].map((_, index) => (
                <div
                  key={`bid-${index}`}
                  className="flex-1 border-b border-border"
                  style={{
                    backgroundColor:
                      index < 10
                        ? `rgba(75, 155, 255, ${0.15 - index * 0.015})`
                        : "transparent",
                  }}
                />
              ))}
            </div>
          </div>

          {/* Ask Side (Right) */}
          <div>
            <div className="h-full flex flex-col">
              {/* Ask levels - Red gradient at top */}
              {[...Array(20)].map((_, index) => (
                <div
                  key={`ask-${index}`}
                  className="flex-1 border-b border-border"
                  style={{
                    backgroundColor:
                      index < 3
                        ? `rgba(255, 75, 75, ${0.15 - index * 0.05})`
                        : "transparent",
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Action Buttons */}
      <div className="flex">
        <button
          onClick={handleSell}
          className="flex-1 bg-red-600 py-5 text-foreground text-2xl font-normal"
        >
          Sell
        </button>
        <button
          onClick={handleClose}
          className="flex-1 bg-gray-600 py-5 text-foreground text-2xl font-normal"
        >
          Close
        </button>
        <button
          onClick={handleBuy}
          className="flex-1 bg-blue-500 py-5 text-foreground text-2xl font-normal"
        >
          Buy
        </button>
      </div>

      {/* Bottom Bar Indicator */}
      <div className="pb-8 pt-4 flex justify-center">
        <div className="w-32 h-1 bg-white rounded-full" />
      </div>
    </div>
  );
}
